import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";
import { Video, Mic, Wifi, CheckCircle2, Camera, PhoneOff } from "lucide-react";

const JITSI_DOMAIN = "meet.jit.si";
const JITSI_SCRIPT = `https://${JITSI_DOMAIN}/external_api.js`;

function loadJitsi() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
    let s = document.querySelector("script[data-jitsi]");
    if (s) {
      s.addEventListener("load", () => resolve(window.JitsiMeetExternalAPI));
      s.addEventListener("error", reject);
      return;
    }
    s = document.createElement("script");
    s.src = JITSI_SCRIPT;
    s.async = true;
    s.dataset.jitsi = "1";
    s.onload = () => resolve(window.JitsiMeetExternalAPI);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function VideoRoom() {
  const { apptId } = useParams();
  const { user } = useAuth();
  const [appt, setAppt] = useState(null);
  const [notes, setNotes] = useState(null);
  const [checked, setChecked] = useState({ cam: false, mic: false, net: false });
  const [inCall, setInCall] = useState(false);
  const jitsiRef = useRef(null);
  const apiRef = useRef(null);

  useEffect(() => {
    api.get(`/appointments/${apptId}`).then(r => setAppt(r.data));
    api.get(`/appointments/${apptId}/notes`).then(r => setNotes(r.data)).catch(()=>{});
  }, [apptId]);

  useEffect(() => {
    let mounted = true;
    if (!inCall) return;
    (async () => {
      try {
        const JitsiCtor = await loadJitsi();
        if (!mounted || !jitsiRef.current) return;
        apiRef.current = new JitsiCtor(JITSI_DOMAIN, {
          roomName: `CareMatch-${apptId}`,
          parentNode: jitsiRef.current,
          width: "100%",
          height: "100%",
          userInfo: {
            displayName: `${user?.role === "doctor" ? "Dr. " : ""}${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Guest",
            email: user?.email || "",
          },
          configOverwrite: {
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            DEFAULT_BACKGROUND: "#1a2b4a",
            TOOLBAR_BUTTONS: [
              "microphone", "camera", "desktop", "chat", "raisehand",
              "tileview", "hangup", "fullscreen", "settings",
            ],
          },
        });
        apiRef.current.addListener("readyToClose", () => setInCall(false));
        apiRef.current.addListener("videoConferenceLeft", () => setInCall(false));
      } catch (e) {
        toast.error("Could not load the video room. Please try again.");
        setInCall(false);
      }
    })();
    return () => {
      mounted = false;
      try { apiRef.current?.dispose(); } catch {}
      apiRef.current = null;
    };
  }, [inCall, apptId, user]);

  const ready = checked.cam && checked.mic && checked.net;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="cm-eyebrow mb-2">Video consultation</div>
          <h1 className="cm-h2">{appt?.doctor_name || "Consultation"}</h1>
          <div className="text-sm text-[#475569] mt-1">{(appt?.slot_datetime||"").replace("T"," ").slice(0,16)}</div>
        </div>
        {inCall && (
          <button onClick={()=>setInCall(false)} className="cm-btn-outline text-sm" data-testid="leave-call"><PhoneOff size={14}/>Leave</button>
        )}
      </div>

      {!inCall ? (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 cm-card">
            <h3 className="font-semibold text-[#1a2b4a] mb-4">Pre-call check</h3>
            <div className="space-y-3">
              <CheckItem icon={<Camera size={18}/>} label="Camera" done={checked.cam} onCheck={()=>setChecked(c=>({...c,cam:true}))} testid="check-cam"/>
              <CheckItem icon={<Mic size={18}/>} label="Microphone" done={checked.mic} onCheck={()=>setChecked(c=>({...c,mic:true}))} testid="check-mic"/>
              <CheckItem icon={<Wifi size={18}/>} label="Connection" done={checked.net} onCheck={()=>setChecked(c=>({...c,net:true}))} testid="check-net"/>
            </div>
            <button className="cm-btn-primary w-full mt-6" disabled={!ready} onClick={()=>setInCall(true)} data-testid="join-call-btn">
              <Video size={16}/> {ready ? "Join secure video room" : "Complete checks to continue"}
            </button>
            <p className="text-xs text-[#94a3b8] text-center mt-3">Encrypted WebRTC video via Jitsi. Grant camera &amp; microphone when your browser asks.</p>
          </div>
          <div className="space-y-4">
            <div className="cm-card">
              <div className="cm-eyebrow mb-2">Before you join</div>
              <ul className="text-sm text-[#475569] space-y-2">
                <li>· Find a quiet, well-lit space</li>
                <li>· Have any medications to hand</li>
                <li>· Prepare your top questions</li>
              </ul>
            </div>
            {notes && (
              <div className="cm-card">
                <div className="cm-eyebrow mb-2">Doctor's notes</div>
                <div className="text-sm space-y-1">
                  {notes.summary && <div><b>Summary:</b> {notes.summary}</div>}
                  {notes.advice && <div><b>Advice:</b> {notes.advice}</div>}
                  {notes.medications && <div><b>Medications:</b> {notes.medications}</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="cm-card p-0 overflow-hidden">
          <div ref={jitsiRef} className="w-full bg-[#1a2b4a]" style={{ height: "70vh", minHeight: 480 }} data-testid="jitsi-container"/>
          <div className="p-3 bg-white border-t border-[#e2e8f0] flex items-center justify-between text-xs text-[#475569]">
            <div>Secure WebRTC video · Room <span className="font-mono">CareMatch-{apptId?.slice(-8)}</span></div>
            {user?.role === "doctor" && (
              <Link to={`/doctor/consultation/${apptId}`} className="text-[#4a90d9] font-semibold" data-testid="back-to-consult">Back to consultation</Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const CheckItem = ({ icon, label, done, onCheck, testid }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border border-[#e2e8f0]">
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${done ? "bg-[#eaf7f0] text-[#3daa6e]" : "bg-[#ebf3fc] text-[#4a90d9]"}`}>{done ? <CheckCircle2 size={18}/> : icon}</div>
      <div className="text-sm font-medium text-[#1a2b4a]">{label}</div>
    </div>
    {done ? <span className="text-xs text-[#3daa6e] font-semibold">OK</span> : <button className="cm-btn-outline py-1.5 px-3 text-xs" onClick={onCheck} data-testid={testid}>Test</button>}
  </div>
);
